import { Injectable, computed, signal } from '@angular/core';

import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import { Analytics, getAnalytics, isSupported as isAnalyticsSupported } from 'firebase/analytics';
import { Auth, getAuth } from 'firebase/auth';
import { Firestore, getFirestore } from 'firebase/firestore';
import { Functions, getFunctions } from 'firebase/functions';
import { FirebaseStorage, getStorage } from 'firebase/storage';
import type { Region } from '@crewdle/auth-bridge';

import { environment } from '../../../environments/environment';
import {
  HOME_REGION,
  RegionEndpoints,
  RegionUnavailableError,
  cloudFunctionUrl,
  regionalFirebaseConfigs,
} from '../region';

/** Which project the active org's data lives in. `app: null` = region unavailable. */
interface OrgBinding {
  orgId: string | null;
  region: Region;
  app: FirebaseApp | null;
  error: string | null;
}

/**
 * The Firebase seam. Two handles (data residency, Milestone B):
 *
 * - GLOBAL (`globalApp`, `globalFirestore`, `auth`): the default ca project —
 *   `users/{uid}` profile (theme, language, currentOrganizationId,
 *   organizationIds), orgDirectory, catalog, white-label/home-hub.
 *   Profile WRITES always go here (single-writer; the server mirrors the doc
 *   into each regional project).
 * - ORG (`app`, `firestore`, `storage`, `functions`): the active org's
 *   regional project — everything the org owns, including `users/{uid}/<sub>`
 *   content created while working in it, and every callable. Bound by
 *   OrgContextService via `AuthBridge.connectRegion()`; until then (and for
 *   every ca org) it IS the global app — exactly the pre-residency behavior.
 *
 * The org handles are getters: they resolve the CURRENT binding on each use,
 * so call sites must not cache them across an org switch.
 */
@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  readonly globalApp: FirebaseApp;
  /** Global session. The regional app is signed in as the same uid. */
  readonly auth: Auth;
  readonly globalFirestore: Firestore;
  // Lazy — Analytics requires a browser-only `isSupported()` check (it's
  // unavailable in SSR / non-secure contexts). Resolves once on app init.
  readonly analytics: Promise<Analytics | null>;
  /** Remote-region Firebase configs for `connectRegion({ regionalConfigs })`. */
  readonly regionalConfigs = regionalFirebaseConfigs(environment.regions);

  private readonly binding = signal<OrgBinding>({ orgId: null, region: HOME_REGION, app: null, error: null });
  /** Region of the active org (ca until an org is bound). */
  readonly region = computed(() => this.binding().region);
  /** Set when the active org's region has no live stack / config. */
  readonly regionError = computed(() => this.binding().error);
  /** Bumps on every (re)binding — effects that own org listeners can track it. */
  readonly orgAppVersion = signal(0);

  constructor() {
    this.globalApp = getApps().length ? getApp() : initializeApp(environment.firebase);
    this.auth = getAuth(this.globalApp);
    this.globalFirestore = getFirestore(this.globalApp);
    this.binding.set({ orgId: null, region: HOME_REGION, app: this.globalApp, error: null });
    this.analytics = isAnalyticsSupported()
      .then((ok) => (ok ? getAnalytics(this.globalApp) : null))
      .catch(() => null);
  }

  /** The active org's Firebase app (regional project; the global app for ca). */
  get app(): FirebaseApp {
    const { app, orgId, error } = this.binding();
    if (!app) throw new RegionUnavailableError(orgId, error ?? undefined);
    return app;
  }

  get firestore(): Firestore {
    return getFirestore(this.app);
  }

  get storage(): FirebaseStorage {
    return getStorage(this.app);
  }

  /** Storage of the GLOBAL project (profile pictures under users/{uid}/profile-picture). */
  get globalStorage(): FirebaseStorage {
    return getStorage(this.globalApp);
  }

  get functions(): Functions {
    return getFunctions(this.app, this.endpoints().functionsRegion);
  }

  /** Functions region / cloudlet URL of the active org's region. */
  endpoints(): RegionEndpoints {
    const { region, orgId } = this.binding();
    const endpoints = environment.regions[region];
    if (!endpoints) throw new RegionUnavailableError(orgId, `no endpoints for region "${region}"`);
    return endpoints;
  }

  /** Cloudlet base URL of the active org's region (api[.<region>][.stg].crewdle.ai). */
  apiUrl(): string {
    return this.endpoints().apiUrl;
  }

  /** URL of an HTTP (non-callable) function in the active org's regional project. */
  functionUrl(name: string): string {
    const projectId = this.app.options.projectId as string;
    return cloudFunctionUrl(projectId, this.endpoints().functionsRegion, name);
  }

  /** Point the org handles at the org's regional app (from `connectRegion`). */
  bindOrg(orgId: string, region: Region, app: FirebaseApp): void {
    const current = this.binding();
    if (current.orgId === orgId && current.app === app && !current.error) return;
    this.binding.set({ orgId, region, app, error: null });
    this.orgAppVersion.update((v) => v + 1);
  }

  /**
   * The active org's region can't be reached (no config in this build, region
   * not live, region-token refused). Org handles throw until the next bind —
   * never fall back to the ca project.
   */
  bindUnavailable(orgId: string, error: string): void {
    this.binding.set({ orgId, region: this.binding().region, app: null, error });
    this.orgAppVersion.update((v) => v + 1);
  }
}
